import { useState } from "react";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/d7a7pp1nauq9gnuqndp0";

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TIME_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM"
];

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const [step, setStep] = useState<"form" | "success">("form");
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    notes: "",
  });

  // Generate next 7 days
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i + 1); // Start from tomorrow
    return {
      value: d.toISOString().split("T")[0],
      label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
    };
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => {
        if (val) params.append(key, val);
      });

      await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      setStep("success");
    } catch {
      // Still show success — form submission is best-effort
      setStep("success");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field: string, value: string) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Book a Call</h3>
            <p className="text-xs text-gray-400">15-min intro with our team</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {step === "success" ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-double-line text-[#16a34a] text-3xl" />
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">You're all set!</h4>
            <p className="text-sm text-gray-500 mb-6">
              We'll send a calendar invite to {formData.email} shortly.
            </p>
            <button
              onClick={onClose}
              className="bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition cursor-pointer"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Name + Company */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input
                  required
                  type="text"
                  value={formData.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Company *</label>
                <input
                  required
                  type="text"
                  value={formData.company}
                  onChange={(e) => update("company", e.target.value)}
                  placeholder="Solar Co"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Email + Phone */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                <input
                  required
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="john@company.com"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="(555) 000-0000"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Date */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Preferred Date *</label>
              <div className="grid grid-cols-4 gap-2">
                {dates.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => update("date", d.value)}
                    className={`px-2 py-2 rounded-lg text-xs font-medium border transition cursor-pointer ${
                      formData.date === d.value
                        ? "bg-[#16a34a] text-white border-[#16a34a]"
                        : "bg-white text-gray-600 border-gray-200 hover:border-green-400"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Preferred Time *</label>
              <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto p-1">
                {TIME_SLOTS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => update("time", t)}
                    className={`px-2 py-2 rounded-lg text-xs font-medium border transition cursor-pointer whitespace-nowrap ${
                      formData.time === t
                        ? "bg-[#16a34a] text-white border-[#16a34a]"
                        : "bg-white text-gray-600 border-gray-200 hover:border-green-400"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="What would you like to discuss?"
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.notes.length}/500</p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || !formData.name || !formData.company || !formData.email || !formData.date || !formData.time}
              className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white font-semibold py-3 rounded-lg text-sm transition cursor-pointer flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Booking...
                </>
              ) : (
                <>Confirm Booking <i className="ri-arrow-right-line" /></>
              )}
            </button>

            <p className="text-center text-xs text-gray-400">
              <i className="ri-calendar-check-line mr-1" />
              You'll receive a calendar confirmation via email
            </p>
          </form>
        )}
      </div>
    </div>
  );
}